/**
 * Live event log for a screening run: filter chips, one line per event, autoscroll.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import type { ScreeningLogEvent } from "../../api/clinicalTrialsApiTypes";

/**
 * Render a tool result's content blocks as prose when that is all they hold.
 *
 * @param content - The event's `content` field
 * @returns Human-readable text
 */
function stringify(content: unknown): string {
  if (!content) return "";
  if (typeof content === "string") return content;
  if (!Array.isArray(content)) return JSON.stringify(content);
  const text = content
    .map((block) =>
      block &&
      typeof block === "object" &&
      (block as { type?: string }).type === "text"
        ? String((block as { text?: string }).text ?? "")
        : "",
    )
    .join("");
  return text || JSON.stringify(content);
}

/**
 * Reduce one event to the single line shown in the log.
 *
 * @param event - Log event
 * @returns The line body
 */
function body(event: ScreeningLogEvent): string {
  if (event.event === "tool_use") {
    return `${event.tool ?? ""} ${JSON.stringify(event.input ?? {})}`;
  }
  if (event.text) return event.text;
  const content = stringify(event.content);
  if (content) return content;
  const rest = Object.fromEntries(
    Object.entries(event).filter(
      ([key]) => !["ts", "event", "item"].includes(key),
    ),
  );
  return JSON.stringify(rest);
}

interface Props {
  events: ScreeningLogEvent[];
}

/**
 * The run log panel.
 *
 * @param props - The events collected so far
 * @returns The log panel
 */
export function RunLog({ events }: Props) {
  const [filter, setFilter] = useState("all");
  const [autoscroll, setAutoscroll] = useState(true);
  const box = useRef<HTMLDivElement | null>(null);

  const kinds = useMemo(() => {
    const seen = new Set(events.map((event) => event.event));
    return ["all", ...Array.from(seen).sort()];
  }, [events]);

  const shown = useMemo(
    () =>
      filter === "all"
        ? events
        : events.filter((event) => event.event === filter),
    [events, filter],
  );

  useEffect(() => {
    if (autoscroll && box.current)
      box.current.scrollTop = box.current.scrollHeight;
  }, [shown, autoscroll]);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-2">
        {kinds.map((kind) => (
          <button
            key={kind}
            onClick={() => setFilter(kind)}
            className="px-2.5 py-1 rounded-full text-xs font-medium border"
            style={{
              backgroundColor:
                filter === kind ? "var(--color-primary)" : "transparent",
              color: filter === kind ? "#fff" : "var(--color-foreground)",
              borderColor: "var(--color-primary)",
              cursor: "pointer",
            }}
          >
            {kind}
          </button>
        ))}
        <label className="ml-auto flex items-center gap-2 text-xs text-gray-600">
          <input
            type="checkbox"
            checked={autoscroll}
            onChange={(e) => setAutoscroll(e.target.checked)}
          />
          Autoscroll
        </label>
      </div>
      <div
        ref={box}
        className="h-96 overflow-auto rounded-md border border-gray-200 bg-gray-50 p-3 font-mono text-xs"
      >
        {shown.length === 0 ? (
          <p className="text-gray-400">Waiting for events…</p>
        ) : (
          shown.map((event, index) => (
            <div
              key={index}
              className="flex gap-2 py-0.5 border-b border-gray-100"
            >
              <span className="shrink-0 w-36 text-gray-500">{event.event}</span>
              <span className="shrink-0 w-28 truncate text-gray-400">
                {event.item ?? ""}
              </span>
              <span className="whitespace-pre-wrap break-words">
                {body(event)}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
